import { Head, router } from "@inertiajs/react"
import { MessageCircle, Plus, Search } from "lucide-react"
import { useState, useMemo } from "react"
import { MemberLayout } from "../components/MemberLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"
import { CategoryBadge, UrgencyBadge, ReviewStatusBadge } from "@/components/group-features/DiscussionBadges"
import { timeAgo } from "@/lib/time-ago"

const CATEGORY_ORDER = { professional: 0, personal: 1, spiritual: 2 }

// Pending-first is a stable primary partition ahead of whatever column the
// viewer has sorted by (R12) -- the server already orders this way, but the
// client re-sorts on every render, so the guarantee has to live here too.
const REVIEW_STATUS_ORDER = { pending_review: 0, posted: 1 }

// Urgency is now a 1-10 integer. The filter narrows by band rather than by
// individual value so the dropdown stays short.
const URGENCY_RANGES = {
  "1-3": [1, 3],
  "4-6": [4, 6],
  "7-10": [7, 10],
}

// Comparator that puts null/undefined values at the END regardless of direction.
function compareWithNullsLast(a, b, direction) {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (a < b) return direction === "asc" ? -1 : 1
  if (a > b) return direction === "asc" ? 1 : -1
  return 0
}

export default function CrucialConversationsIndex({
  currentUser,
  crucialConversations = [],
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [urgencyFilter, setUrgencyFilter] = useState("all")
  const [memberFilter, setMemberFilter] = useState("all")
  // Default: newest submitted first (task #517), applied *within* each review
  // group -- pending_review still pins above posted (task #426, R12).
  const [sortKey, setSortKey] = useState("createdAt")
  const [direction, setDirection] = useState("desc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    // desc → clear back to default (createdAt desc)
    setSortKey("createdAt")
    setDirection("desc")
  }

  const memberOptions = useMemo(() => {
    const byId = new Map()
    crucialConversations.forEach((c) => {
      if (c.author?.id != null && !byId.has(c.author.id)) {
        byId.set(c.author.id, c.author)
      }
    })
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [crucialConversations])

  const filtered = useMemo(() => {
    let results = crucialConversations
    if (categoryFilter !== "all") results = results.filter((c) => c.category === categoryFilter)
    if (urgencyFilter !== "all") {
      const [min, max] = URGENCY_RANGES[urgencyFilter]
      results = results.filter((c) => c.urgency != null && c.urgency >= min && c.urgency <= max)
    }
    if (memberFilter !== "all") {
      results = results.filter((c) => String(c.author?.id) === memberFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter((c) => c.topicIssue.toLowerCase().includes(q))
    }
    const sorted = [...results].sort((a, b) => {
      const reviewDiff = (REVIEW_STATUS_ORDER[a.reviewStatus] ?? 1) - (REVIEW_STATUS_ORDER[b.reviewStatus] ?? 1)
      if (reviewDiff !== 0) return reviewDiff

      let aVal
      let bVal
      if (sortKey === "createdAt") {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : null
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : null
      } else if (sortKey === "topic") {
        aVal = a.topicIssue?.toLowerCase() || null
        bVal = b.topicIssue?.toLowerCase() || null
      } else if (sortKey === "category") {
        aVal = a.category != null ? CATEGORY_ORDER[a.category] : null
        bVal = b.category != null ? CATEGORY_ORDER[b.category] : null
      } else if (sortKey === "urgency") {
        aVal = a.urgency != null ? a.urgency : null
        bVal = b.urgency != null ? b.urgency : null
      } else if (sortKey === "author") {
        aVal = a.author?.name?.toLowerCase() || null
        bVal = b.author?.name?.toLowerCase() || null
      }
      return compareWithNullsLast(aVal, bVal, direction)
    })
    return sorted
  }, [crucialConversations, categoryFilter, urgencyFilter, memberFilter, searchQuery, sortKey, direction])

  const isEmpty = crucialConversations.length === 0

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="Crucial Conversations - Kingdom Factor" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Crucial Conversations</h1>
            <p className="text-zinc-600 mt-1">
              Address important topics with your group's wisdom.
            </p>
          </div>
          <Button
            variant="gradient"
            onClick={() => router.visit("/app/member/crucial_conversations/new")}
            className="self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Conversation
          </Button>
        </div>

        {isEmpty && (
          <div className="text-center py-12 text-zinc-500">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No crucial conversations yet.</p>
            <p className="text-sm mt-1">Start one when you need group wisdom.</p>
          </div>
        )}

        {!isEmpty && (
          <>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Search conversations..."
                  className="pl-9 bg-white border-zinc-300"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-40 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="spiritual">Spiritual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                <SelectTrigger className="w-full sm:w-36 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Urgency</SelectItem>
                  <SelectItem value="1-3">Low (1-3)</SelectItem>
                  <SelectItem value="4-6">Medium (4-6)</SelectItem>
                  <SelectItem value="7-10">High (7-10)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={memberFilter} onValueChange={setMemberFilter}>
                <SelectTrigger className="w-full sm:w-44 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  {memberOptions.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filtered.length === 0 ? (
              <p className="text-center py-8 text-zinc-500 text-sm">No matching conversations.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="topic" sortKey={sortKey} direction={direction} onSort={handleSort}>
                      Conversation
                    </SortableHeader>
                    <SortableHeader column="category" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-28">
                      Category
                    </SortableHeader>
                    <SortableHeader column="urgency" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-24">
                      Urgency
                    </SortableHeader>
                    <SortableHeader column="author" sortKey={sortKey} direction={direction} onSort={handleSort} className="hidden sm:table-cell">
                      Author
                    </SortableHeader>
                    <SortableHeader column="createdAt" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-20 text-right">
                      Date
                    </SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((cc) => (
                    <TableRow
                      key={cc.id}
                      className="cursor-pointer"
                      onClick={() => router.visit(`/app/member/crucial_conversations/${cc.id}`)}
                    >
                      <TableCell className="whitespace-normal">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-zinc-900 line-clamp-2">
                            {cc.topicIssue}
                          </span>
                          <ReviewStatusBadge reviewStatus={cc.reviewStatus} />
                        </div>
                      </TableCell>
                      <TableCell><CategoryBadge category={cc.category} /></TableCell>
                      <TableCell><UrgencyBadge urgency={cc.urgency} /></TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-zinc-600">
                        {cc.author.name}
                      </TableCell>
                      <TableCell className="text-right text-sm text-zinc-500">
                        {timeAgo(cc.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>
    </MemberLayout>
  )
}
